/**
 * ═══════════════════════════════════════════════════════════════════════
 * TenantChannel — Tenant-Scoped Event Subscription Manager
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Connects the RealtimeSyncService + Zustand stores for a specific
 * organisation (tenant).  Handles:
 *
 *   1. Wiring EventBus events → Zustand store updates
 *   2. Emitting local mutation events (optimistic UI → EventBus)
 *   3. Reconciliation on reconnect (re-fetch truth from server)
 *   4. Scope-aware filtering (doctor-scoped users ignore other doctors' events)
 */

import eventBus from './EventBus';
import realtimeSyncService, { REALTIME_EVENTS } from './RealtimeSyncService';

class TenantChannel {
  constructor() {
    this._tenantId = null;
    this._unsubscribers = [];
    this._storeBindings = new Map(); // storeName → { getState, setState, handlers }
    this._lastReconciledAt = 0;      // throttle store reconciliation
  }

  // ── Connect to a tenant ────────────────────────────────────────────
  /**
   * @param {Object} opts
   * @param {string}   opts.tenantId
   * @param {Function} opts.authTokenProvider - async () => freshJWT
   * @param {Function} opts.pollFetcher  - async () => serverData
   * @param {Object}   opts.scope        - { doctorId } for scoped roles
   */
  connect(opts) {
    const { tenantId, authTokenProvider, pollFetcher, scope } = opts;

    if (this._tenantId) this.disconnect();
    this._tenantId = tenantId;
    this._scope = scope || {};

    // Start the transport
    realtimeSyncService.connect({ tenantId, authTokenProvider, pollFetcher });

    // Subscribe to reconnect events for reconciliation
    this._subscribe(REALTIME_EVENTS.CONNECTED, (event) => {
      this._onReconnected(event);
    });

    this._subscribe(REALTIME_EVENTS.DISCONNECTED, () => {
      // Could show a toast / banner here
    });
  }

  // ── Disconnect ─────────────────────────────────────────────────────
  disconnect() {
    this._unsubscribers.forEach((unsub) => unsub());
    this._unsubscribers = [];
    this._storeBindings.clear();
    realtimeSyncService.disconnect();
    this._tenantId = null;
  }

  // ── Register a Zustand store to receive realtime events ────────────
  /**
   * @param {string}   storeName  - e.g. 'visit', 'queue'
   * @param {Object}   handlers   - { [REALTIME_EVENT_TYPE]: (state, event) => partialState }
   * @param {Function} getState   - store.getState
   * @param {Function} setState   - store.setState
   */
  bindStore(storeName, { handlers, getState, setState }) {
    this._storeBindings.set(storeName, { handlers, getState, setState });

    // Wire up each handler
    Object.entries(handlers).forEach(([eventType, handler]) => {
      this._subscribe(eventType, (event) => {
        // Tenant scope check
        if (event.tenantId && event.tenantId !== this._tenantId) return;

        // Skip local events (we already applied them optimistically)
        if (event.source === 'local') return;

        const currentState = getState();
        const partial = handler(currentState, event);
        if (partial && Object.keys(partial).length > 0) {
          setState(partial);
        }
      });
    });
  }

  // ── Emit a local mutation event (for cross-tab + future WS) ───────
  emitLocal(type, payload, causedBy) {
    eventBus.emit({
      type,
      payload,
      tenantId: this._tenantId,
      source: 'local',
      timestamp: Date.now(),
      causedBy,
      eventId: `local_${type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    });
  }

  // ── Private ────────────────────────────────────────────────────────

  _subscribe(eventType, handler) {
    const unsub = eventBus.on(eventType, handler);
    this._unsubscribers.push(unsub);
    return unsub;
  }

  /**
   * On reconnect, ask all bound stores to reconcile with server truth.
   * Throttled to once per 30s — prevents API hammering during rapid
   * reconnect cycles (each store.reconcile triggers 3+ API calls).
   */
  _onReconnected(_event) {
    const now = Date.now();
    if (now - this._lastReconciledAt < 30_000) {
      return; // skip — too soon since last reconciliation
    }
    this._lastReconciledAt = now;

    this._storeBindings.forEach(({ getState }) => {
      const state = getState();
      if (typeof state.reconcile === 'function') {
        state.reconcile();
      }
    });
  }

  get isConnected() {
    return realtimeSyncService.isConnected;
  }

  get tenantId() {
    return this._tenantId;
  }
}

// Singleton
const tenantChannel = new TenantChannel();
export default tenantChannel;
